import 'client-only';

import { useCallback } from 'react';
import {
  BillingApiSdk,
  ClientInferRequest,
  contract,
  type ClientInferResponses,
} from '@pie/billing-api-sdk';
import { keepPreviousData, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { env } from '@/config/env';
import { ApiError } from '@/lib/api/ApiError';
import { mapPaymentMethodItemToRow, mapTransactionToPayment } from './mappers';
import type { Payment, PaymentMethod } from './types';
import { orderBy } from 'lodash';
import { useAuthContext } from '@/components/AuthContext';

const { client } = new BillingApiSdk({ environment: env.NEXT_PUBLIC_PIE_ENV });

const QUERY_KEYS = {
  policyPaymentMethod: (policyId: string) =>
    ['billingApiSdk', 'getPaymentMethod', policyId] as const,
  accountPaymentMethods: () => ['billingApiSdk', 'getAccountPaymentMethods'] as const,
  accountTransactions: (policyIds?: string[]) =>
    ['billingApiSdk', 'getAccountTransactions', policyIds] as const,
  paymentSession: (policyId: string, paymentMethodType: 'card' | 'ach') =>
    ['billingApiSdk', 'postPaymentSession', policyId, paymentMethodType] as const,
};

const getPaymentMethod = async (
  policyId: string,
  extraHeaders: ClientInferRequest<typeof contract.getPaymentMethod>['extraHeaders']
) => {
  const response = await client.getPaymentMethod({
    extraHeaders,
    params: { policyId },
  });
  if (response.status !== 200) {
    throw new ApiError(response.status, response.body, response.headers);
  }
  return response.body;
};

export const usePolicyPaymentMethod = (policyId: string) => {
  const { getSessionHeaders } = useAuthContext();

  return useQuery({
    queryKey: QUERY_KEYS.policyPaymentMethod(policyId),
    queryFn: async () => {
      const { authorization } = await getSessionHeaders();
      return getPaymentMethod(policyId, { authorization });
    },
  });
};

const getAllAccountPaymentMethods = async (
  extraHeaders: ClientInferRequest<typeof contract.getAccountPaymentMethods>['extraHeaders']
): Promise<PaymentMethod[]> => {
  const allMethods: PaymentMethod[] = [];
  let cursor: string | undefined;

  do {
    const response = await client.getAccountPaymentMethods({
      extraHeaders,
      query: { cursor },
    });
    if (response.status !== 200) {
      throw new ApiError(response.status, response.body, response.headers);
    }
    for (const item of response.body.items) {
      // Contract says every item has ≥1 policy, but the type allows an empty
      // array — skip so downstream row rendering can assume a primary policy.
      if (item.policies.length === 0) continue;
      allMethods.push(mapPaymentMethodItemToRow(item));
    }
    cursor = response.body.cursor ?? undefined;
  } while (cursor);

  return allMethods;
};

export const useAccountPaymentMethods = (options?: UseQueryOptions<PaymentMethod[]>) => {
  const { getSessionHeaders } = useAuthContext();

  const {
    data: paymentMethods,
    isLoading,
    isError,
    isFetching,
  } = useQuery<PaymentMethod[]>({
    ...options,
    queryKey: QUERY_KEYS.accountPaymentMethods(),
    queryFn: async () => {
      const { authorization } = await getSessionHeaders();
      return getAllAccountPaymentMethods({ authorization });
    },
  });
  return { paymentMethods, isLoading, isError, isFetching };
};

type FetchAccountTransactionsArgs = {
  cursor: string | undefined;
  policyIds?: string[];
  limit: number;
};

export const fetchAccountTransactions = async (
  { cursor, policyIds, limit }: FetchAccountTransactionsArgs,
  extraHeaders: ClientInferRequest<typeof contract.getAccountTransactions>['extraHeaders']
): Promise<{
  items: Payment[];
  cursor: string | null;
  totalCount: number;
}> => {
  const response = await client.getAccountTransactions({
    extraHeaders,
    query: { policyIds, cursor, limit },
  });
  if (response.status !== 200) {
    throw new ApiError(response.status, response.body, response.headers);
  }
  return {
    items: response.body.items.map((tx, i) => mapTransactionToPayment(tx, i)),
    cursor: response.body.cursor,
    totalCount: response.body.totalCount,
  };
};

export const useAccountTransactions = (policyIds?: string[]) => {
  const { getSessionHeaders } = useAuthContext();

  return useQuery({
    queryKey: QUERY_KEYS.accountTransactions(policyIds),
    queryFn: async () => {
      const { authorization } = await getSessionHeaders();
      return await fetchAccountTransactions(
        { cursor: undefined, policyIds, limit: 100 },
        { authorization }
      );
    },
  });
};

export type PaymentSession = Extract<
  ClientInferResponses<typeof contract.postPaymentSession>,
  { status: 200 }
>['body'];

export type PaymentMethodType = ClientInferRequest<
  typeof contract.postPaymentSession
>['body']['paymentMethodType'];

const mockPaymentSessionEnabled = () =>
  env.NEXT_PUBLIC_MOCK_PAYMENT_SESSION &&
  env.NODE_ENV !== 'production' &&
  !['post-prod', 'production'].includes(env.NEXT_PUBLIC_PIE_ENV ?? '');

const createPaymentSession = async (
  policyId: string,
  paymentMethodType: PaymentMethodType,
  extraHeaders: ClientInferRequest<typeof contract.postPaymentSession>['extraHeaders']
): Promise<PaymentSession> => {
  const response = await client.postPaymentSession({
    body: { paymentMethodType },
    extraHeaders,
    params: { policyId },
  });
  if (response.status !== 200) {
    throw new ApiError(response.status, response.body, response.headers);
  }
  const session = response.body;

  // Local-dev only: re-mint against dev tenants. See docs/payment-platforms.md.
  if (mockPaymentSessionEnabled()) {
    const params = new URLSearchParams({
      paymentProcessor: session.platform,
      type: paymentMethodType,
    });
    if (session.platform === 'zuora' && session.zuora?.accountId) {
      params.set('accountId', session.zuora.accountId);
    }
    const devResponse = await fetch(`/api/dev/payment-session?${params.toString()}`);
    const devBody = await devResponse.json();
    if (!devResponse.ok) {
      throw new ApiError(devResponse.status, devBody, devResponse.headers);
    }
    return devBody as PaymentSession;
  }

  return session;
};

/**
 * Creates (and re-creates) a payment session for the edit-payment dialog.
 *
 * Keyed on the API method type (`card` / `ach`) so Credit/Debit share one
 * session (no refetch), while card↔ach refetches. `gcTime: 0` drops inactive
 * sessions so switching back mints a fresh single-use token (Zuora rejects a
 * reused render token). `keepPreviousData` keeps the platform branch stable
 * across a switch refetch. `retry: false` surfaces a failed fetch immediately.
 */
export const usePaymentSession = (policyId: string, paymentMethodType: 'card' | 'ach') => {
  const { getSessionHeaders } = useAuthContext();

  return useQuery<PaymentSession>({
    queryKey: QUERY_KEYS.paymentSession(policyId, paymentMethodType),
    queryFn: async () => {
      const { authorization } = await getSessionHeaders();
      return createPaymentSession(policyId, paymentMethodType, { authorization });
    },
    gcTime: 0,
    staleTime: 0,
    retry: false,
    placeholderData: keepPreviousData,
  });
};

/** Invalidates the policy-level payment method query (background refetch). */
export const useInvalidatePolicyPaymentMethod = (policyId: string) => {
  const queryClient = useQueryClient();
  return useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.policyPaymentMethod(policyId),
    });
  }, [queryClient, policyId]);
};

/** Invalidates the account-wide payment methods query (background refetch). */
export const useInvalidateAccountPaymentMethods = () => {
  const queryClient = useQueryClient();
  return useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.accountPaymentMethods(),
    });
  }, [queryClient]);
};

export const getLastPayment = (payments: Payment[] = []) => {
  const [lastPayment] = orderBy(payments, 'date', 'desc').filter(
    (payment) => payment.status === 'completed' && payment.amount > 0
  );

  return lastPayment;
};

export type ZuoraSession = NonNullable<PaymentSession['zuora']>;
export type StripeSession = NonNullable<PaymentSession['stripe']>;

export const isSessionValid = (session: PaymentSession | undefined) => {
  return isStripeSession(session) || isZuoraSession(session);
};

export const isZuoraSession = (
  session: PaymentSession | undefined
): session is { platform: 'zuora'; zuora: ZuoraSession } => {
  return session?.platform === 'zuora' && session.zuora !== undefined;
};

export const isStripeSession = (
  session: PaymentSession | undefined
): session is { platform: 'stripe'; stripe: StripeSession } => {
  return session?.platform === 'stripe' && session.stripe !== undefined;
};
